import React, { useState, useEffect } from 'react';
import { Plus, Pencil, X, Star, MessageSquareQuote } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import ImageField from '../../components/ImageField';
import { toast } from 'sonner';

const EMPTY = { name: '', city: '', product: '', rating: 5, text: '', date: '', photo: '', video: '', source: 'interna', published: false, featured: false };
const GOOGLE_G = (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
);

export default function AdminOpiniones() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [products, setProducts] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [tab, setTab] = useState('lista');
  const [gConfig, setGConfig] = useState(null);
  const [scme, setScme] = useState(null);

  useEffect(() => {
    api.getReviews(true).then(setReviews).catch(() => {});
    api.getProducts(true).then(setProducts).catch(() => {});
    api.getGoogleReviewsConfig().then(setGConfig).catch(() => {});
    api.getScmeSettings().then(setScme).catch(() => {});
    api.getValueLists().then((lists) => setCityOptions(lists.find((l) => l.id === 'ciudades')?.values.filter((v) => v.active) || [])).catch(() => {});
  }, []);

  const toggleModule = async (v) => {
    try {
      const updated = await api.updateScmeSettings({ reviewsSectionEnabled: v });
      setScme(updated);
      toast.success(v ? 'Módulo de opiniones visible en la web' : 'Módulo de opiniones oculto en la web');
    } catch { toast.error('Error al actualizar'); }
  };

  const saveGConfig = async () => {
    try {
      const { id, ...data } = gConfig;
      const updated = await api.updateGoogleReviewsConfig(data);
      setGConfig(updated);
      toast.success('Configuración de Google Reviews guardada');
    } catch { toast.error('Error al guardar'); }
  };

  const toggle = async (r, field) => {
    try {
      const updated = await api.updateReview(r.id, { [field]: !r[field] });
      setReviews((prev) => prev.map((x) => x.id === r.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.text.trim()) { toast.error('Nombre y texto son obligatorios'); return; }
    try {
      if (editing.id) {
        const { id, ...data } = editing;
        const updated = await api.updateReview(id, data);
        setReviews((prev) => prev.map((r) => r.id === id ? updated : r));
      } else {
        const created = await api.createReview(editing);
        setReviews((prev) => [created, ...prev]);
      }
      setEditing(null);
      toast.success('Opinión guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-opiniones">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <MessageSquareQuote className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Opiniones</h1>
          </div>
          <p className="text-sm text-stone-500">Moderación desde MATRIZ. Las publicadas aparecen en la home (destacadas primero).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-review-button">
          <Plus className="w-4 h-4" /> Nueva opinión
        </button>
      </div>

      {scme && (
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 mb-6 ${scme.reviewsSectionEnabled !== false ? 'bg-teal-50 border-teal-200' : 'bg-stone-100 border-stone-300'}`} data-testid="reviews-module-panel">
          <Switch checked={scme.reviewsSectionEnabled !== false} onCheckedChange={toggleModule} data-testid="reviews-module-switch" />
          <div>
            <p className="text-sm font-bold text-stone-800">Módulo de opiniones {scme.reviewsSectionEnabled !== false ? 'visible en la web' : 'oculto en la web'}</p>
            <p className="text-xs text-stone-500">Interruptor global: oculta o muestra toda la sección de opiniones de la home (independiente de la moderación individual y del bloque Google).</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('lista')} className={`px-4 py-2 text-sm font-bold rounded-full border transition-colors ${tab === 'lista' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300'}`} data-testid="reviews-tab-lista">Opiniones</button>
        <button onClick={() => setTab('google')} className={`px-4 py-2 text-sm font-bold rounded-full border transition-colors flex items-center gap-1.5 ${tab === 'google' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300'}`} data-testid="reviews-tab-google">{GOOGLE_G} Google Reviews</button>
      </div>

      {tab === 'google' && gConfig && (
        <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl space-y-5" data-testid="google-reviews-config">
          <div className="flex items-center justify-between border-b border-stone-100 pb-4">
            <div>
              <p className="font-bold text-stone-800 text-sm">Integración Google Reviews</p>
              <p className="text-xs text-stone-500">Activa el bloque de confianza en la portada (★ media + total de reseñas + enlace al perfil).</p>
            </div>
            <Switch checked={!!gConfig.enabled} onCheckedChange={(v) => setGConfig({ ...gConfig, enabled: v })} data-testid="greviews-enabled" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">URL Perfil Google Business</label>
            <input value={gConfig.profileUrl} onChange={(e) => setGConfig({ ...gConfig, profileUrl: e.target.value })} placeholder="https://g.page/r/..." className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="greviews-url" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Valoración media</label>
              <input type="number" step="0.1" min="1" max="5" value={gConfig.averageRating} onChange={(e) => setGConfig({ ...gConfig, averageRating: parseFloat(e.target.value) || 0 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="greviews-average" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Total de reseñas en Google</label>
              <input type="number" min="0" value={gConfig.totalReviews} onChange={(e) => setGConfig({ ...gConfig, totalReviews: parseInt(e.target.value) || 0 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="greviews-total" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Máx. opiniones visibles</label>
              <input type="number" min="1" max="12" value={gConfig.maxVisible} onChange={(e) => setGConfig({ ...gConfig, maxVisible: parseInt(e.target.value) || 6 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="greviews-max" />
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-stone-700"><Switch checked={!!gConfig.showAverage} onCheckedChange={(v) => setGConfig({ ...gConfig, showAverage: v })} data-testid="greviews-show-average" /> Mostrar valoración media</label>
            <label className="flex items-center gap-2 text-sm text-stone-700"><Switch checked={!!gConfig.showTotal} onCheckedChange={(v) => setGConfig({ ...gConfig, showTotal: v })} data-testid="greviews-show-total" /> Mostrar número total</label>
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-2">Modo de visualización en portada</label>
            <div className="flex flex-wrap gap-2">
              {[['internas', 'Solo internas'], ['google', 'Solo Google Reviews'], ['combinadas', 'Combinadas']].map(([v, l]) => (
                <button key={v} onClick={() => setGConfig({ ...gConfig, displayMode: v })} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${gConfig.displayMode === v ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300'}`} data-testid={`greviews-mode-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="border-t border-stone-100 pt-4 flex items-center gap-3">
            <button onClick={saveGConfig} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="greviews-save">Guardar configuración</button>
            <span className="text-xs text-stone-400">Las reseñas de Google se añaden desde "Nueva opinión" seleccionando origen Google.</span>
          </div>
        </div>
      )}

      {tab === 'lista' && (
      <>

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-8" data-testid="review-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-stone-800">{editing.id ? `Editar opinión de ${editing.name}` : 'Nueva opinión'}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Nombre visible *</label>
              <input value={editing.name} onChange={set('name')} placeholder="Laura M." className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="review-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Ciudad</label>
              <select value={editing.city} onChange={set('city')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                <option value="">Selecciona…</option>
                {cityOptions.map((c) => <option key={c.id} value={c.label}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Curso o producto</label>
              <select value={editing.product} onChange={set('product')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                <option value="">Selecciona…</option>
                {products.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Valoración</label>
              <div className="flex items-center gap-1 pt-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setEditing({ ...editing, rating: n })} data-testid={`rating-star-${n}`}>
                    <Star className={`w-6 h-6 transition-colors ${n <= editing.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Origen</label>
              <select value={editing.source || 'interna'} onChange={set('source')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="review-source-select">
                <option value="interna">Opinión interna</option>
                <option value="google">Google Review</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Fecha</label>
              <input type="date" value={editing.date} onChange={set('date')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <ImageField label="Foto del alumno aprobado" value={editing.photo || ''} onChange={(url) => setEditing({ ...editing, photo: url })} testid="review-photo-field" />
            <ImageField label="Vídeo del alumno (se muestra si está activo en Ecosistema SCME)" value={editing.video || ''} onChange={(url) => setEditing({ ...editing, video: url })} type="video" testid="review-video-field" />
            <div className="md:col-span-3">
              <label className="block text-xs font-bold text-stone-500 mb-1">Texto *</label>
              <textarea value={editing.text} onChange={set('text')} rows={3} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="review-text-input" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={save} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-review-button">Guardar opinión</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Alumno</th>
              <th className="px-4 py-3 font-medium">Opinión</th>
              <th className="px-4 py-3 font-medium w-28">Valoración</th>
              <th className="px-4 py-3 font-medium w-24">Publicada</th>
              <th className="px-4 py-3 font-medium w-24">Destacada</th>
              <th className="px-4 py-3 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((r) => (
              <tr key={r.id} className="border-t border-stone-100 align-top" data-testid={`review-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    {r.photo && <img src={r.photo} alt={r.name} className="w-10 h-10 rounded-full object-cover border border-stone-200 shrink-0" data-testid={`review-photo-thumb-${r.id}`} />}
                    <div>
                      <p className="font-bold text-stone-800 flex items-center gap-1.5">{r.name}{r.source === 'google' && <span title="Google Review">{GOOGLE_G}</span>}</p>
                      <p className="text-xs text-stone-400">{r.city} · {r.date}</p>
                      <p className="text-xs text-teal-600">{r.product}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-600 text-xs max-w-md">{r.text}</td>
                <td className="px-4 py-3">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3"><Switch checked={r.published} onCheckedChange={() => toggle(r, 'published')} data-testid={`toggle-published-${r.id}`} /></td>
                <td className="px-4 py-3"><Switch checked={r.featured} onCheckedChange={() => toggle(r, 'featured')} data-testid={`toggle-featured-${r.id}`} /></td>
                <td className="px-4 py-3">
                  <button onClick={() => setEditing({ ...r })} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-review-${r.id}`}><Pencil className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {reviews.length === 0 && <p className="text-center text-stone-400 py-10">No hay opiniones todavía.</p>}
      </div>
      </>
      )}
    </div>
  );
}
